import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import PrintHeader from '@/components/PrintHeader';
import { ARTIFACT_RENDERERS, TYPE_META } from '@/components/library/LessonViewerModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PrintableLesson — auth-protected full-page render of a saved library lesson,
 * intended to be navigated to by the backend PDF worker. There's no AppShell,
 * no navigation — just the print-ready lesson content with the branded header.
 */
export default function PrintableLesson() {
  const { lessonId } = useParams();
  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = sessionStorage.getItem('_t');
        const res = await axios.get(`${API}/library/lessons/${lessonId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setLesson(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      }
    };
    load();
  }, [lessonId]);

  if (error) {
    return (
      <div className="p-8 text-red-700" data-testid="printable-lesson-error">
        <strong>Could not load lesson:</strong> {error}
      </div>
    );
  }
  if (!lesson) {
    return <div className="p-8 text-stone-500" data-testid="printable-lesson-loading">Loading…</div>;
  }

  const meta = TYPE_META[lesson.artifact_type] || TYPE_META.lesson_plan;
  const Renderer = ARTIFACT_RENDERERS[lesson.artifact_type] || ARTIFACT_RENDERERS.lesson_plan;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 print:max-w-none print:px-0 print:py-2" data-testid="printable-lesson">
      <PrintHeader
        resourceType={meta.label}
        resourceTitle={lesson.title}
        grade={lesson.grade_level}
      />

      <div className="mb-6 pb-3 border-b border-[#EBE7E0] print:hidden">
        <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
          {meta.label}
          {lesson.grade_level && <span className="ml-2 text-[#7A93AC]">Grade {lesson.grade_level}</span>}
        </p>
        <h1 className="text-2xl font-serif text-[#14213D] mt-1">{lesson.title}</h1>
      </div>
      <h1 className="hidden print:block text-2xl font-serif text-black mt-2 mb-3">{lesson.title}</h1>

      {lesson.description && (
        <p className="text-sm text-[#4A5568] italic mb-6 leading-relaxed">{lesson.description}</p>
      )}

      <Renderer content={lesson.content || {}} />
    </div>
  );
}
